<template>
  <Container class="!max-w-[1362px]">
    <div class="mb-12 flex flex-col items-center lg:mb-20">
      <SubTitle class="mb-3 text-blue">{{ data?.subtitle }}</SubTitle>
      <Title :custom-style="useDarkMode ? 'text-white' : ''">{{ data?.title }}</Title>
    </div>
    <div class="flex flex-col lg:flex-row">
      <div class="flex-1 lg:pr-4">
        <div
          v-if="data?.cards?.[0]?.image?.url"
          class="relative flex aspect-square overflow-hidden rounded-xl lg:rounded-2xl"
        >
          <NuxtImg
            :src="data?.cards?.[0]?.image?.url"
            :alt="data?.cards?.[0]?.image?.alt"
            class="absolute aspect-[3/2] w-full object-cover object-center lg:aspect-[5/3]"
            loading="lazy"
            width="649"
            height="649"
            sizes="xs:100vw sm:400px md:500px lg:600px xl:649px"
            fit="cover"
          />
          <div class="relative z-10 flex basis-full flex-col justify-between p-3 lg:p-6">
            <div></div>
            <div>
              <h3
                class="mb-1 line-clamp-2 font-serif text-2xl font-bold leading-none text-white lg:text-3xl lg:leading-snug"
              >
                {{ data?.cards?.[0]?.title }}
              </h3>
              <div
                v-if="data?.cards?.[0]?.copy"
                class="mb-3 line-clamp-2 font-sans text-base leading-snug text-white lg:mb-4 lg:line-clamp-5 lg:text-lg [&_*:nth-child(n+2)]:hidden"
                v-html="data?.cards?.[0]?.copy"
              ></div>
              <LinkSecondary
                v-if="data?.cards?.[0]?.link?.url"
                :title="data?.cards?.[0]?.link?.title"
                :link="data?.cards?.[0]?.link?.url"
                :external="data?.cards?.[0]?.link?.type === 'ext'"
              />
            </div>
          </div>
          <div
            :class="
              twMerge(
                'absolute bottom-0 h-3/4 w-full bg-gradient-to-t lg:h-4/5',
                useDarkMode ? 'from-blue-union via-blue-darker' : 'from-blue-darker via-blue-dark',
              )
            "
          ></div>
        </div>
      </div>
      <div
        class="flex flex-1 flex-col justify-center divide-y divide-blue-dark divide-opacity-40 lg:divide-y-[3px] lg:pl-4"
      >
        <!-- BLOCK -->
        <template v-for="(card, index) in data?.cards" :key="card.id">
          <div
            v-if="index > 0 && index < 4"
            class="flex min-h-[185px] flex-row py-4 lg:first:pt-0 lg:last:pb-0"
          >
            <div
              class="relative aspect-square min-w-32 overflow-hidden rounded-xl lg:w-[170px] lg:shrink-0 lg:rounded-2xl"
            >
              <NuxtImg
                :src="card?.image?.url"
                :alt="card?.image?.alt"
                class="absolute h-full w-full object-cover object-center"
                loading="lazy"
                width="300"
                height="300"
                sizes="xs:100 md:150 lg:170"
                fit="cover"
              />
            </div>
            <div class="ml-4 flex flex-col justify-between lg:ml-9">
              <h3
                :class="
                  twMerge(
                    'line-clamp-2 font-serif text-base font-bold lg:mb-4 lg:text-28 lg:leading-snug',
                    useDarkMode ? 'text-white' : 'text-black',
                  )
                "
              >
                {{ card.title }}
              </h3>
              <div
                v-if="card?.copy"
                :class="
                  twMerge(
                    'line-clamp-2 text-sm leading-6 lg:mb-4 lg:text-lg [&_*:nth-child(n+2)]:hidden',
                    useDarkMode ? 'text-white' : 'text-black',
                  )
                "
                v-html="card?.copy"
              ></div>
              <LinkSecondary
                v-if="card?.link?.url"
                :link="card?.link?.url"
                :title="card?.link?.title"
                :external="card?.link?.type === 'ext'"
                type="blue"
              />
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="mb-8 mt-8 flex justify-center lg:mt-14">
      <Link
        v-if="data?.link?.url"
        class="w-full lg:w-auto"
        type="outline"
        :title="data?.link?.title"
        :link="data?.link?.url"
        :external="data?.link?.type === 'ext'"
      />
    </div>
  </Container>
</template>

<script setup lang="ts">
import SubTitle from '../atoms/SubTitle.vue'
import Title from '../atoms/Title.vue'
import Link from '../atoms/Link.vue'
import Container from '../atoms/Container.vue'
import LinkSecondary from '../atoms/LinkSecondary.vue'
import NuxtImg from '../atoms/NuxtImg.vue'
import type { ILinkData, ICardData } from '../../types'
import { computed, inject } from 'vue'
import { twMerge } from 'tailwind-merge'

export interface ICompactNewsBlock {
  id: string
  title?: string
  subtitle?: string
  link?: ILinkData
  cards?: ICardData[]
}

interface Props {
  data: ICompactNewsBlock
}

defineProps<Props>()

const settings = inject<string[]>('settings', [])
const useDarkMode = computed(() => settings.includes('dark_theme') ?? false)
</script>
